import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { useCallback, useMemo } from "react"
import type { MessageDescriptor } from "react-intl"
import { useIntl } from "react-intl"
import type { DealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import type { MainNavigationProps } from "src/components"
import { MainNavigation, navigationMessages } from "src/components"
import { trackEvent } from "src/components/adobeAnalytics"
import { WhatsNewContainer } from "src/containers/whatsNew/WhatsNewContainer"
import { useDirection } from "src/context/translations/useDirection"
import { useConfig, useRoutePrefix } from "src/hooks"
import { NotificationCenterPopoverContainer } from "src/screens/notificationCenter/NotificationCenterPopoverContainer"

interface DealerMainNavigationProps {
	routes: DealerAppRoutes | null
}

export const DealerMainNavigation = ({ routes }: DealerMainNavigationProps) => {
	const config = useConfig()
	const { formatMessage } = useIntl()
	const { dealershipId, dealershipInfo, displayName, environment, locale, permissions, username } = useDealerContext()
	const direction = useDirection(locale)
	const isRTL = useMemo(() => direction === "rtl", [direction])

	const prefixRoute = useRoutePrefix()
	const isFullOfferMode = config.offers.offerMode === "FULL"

	// only formats label when url is truthy, avoiding unnecessary message formatting
	const toArray = useCallback(
		(
			url: string | undefined | { disabledHint: string },
			label: string | MessageDescriptor,
			key: string,
			isExternalUrl = false,
			isNew = false,
		): MainNavigationProps["navItems"] =>
			url
				? [
						{
							isExternalUrl,
							isNew,
							key,
							label: typeof label === "string" ? label : formatMessage(label),
							url,
						},
					]
				: [],
		[formatMessage],
	)

	const navItems = useMemo<MainNavigationProps["navItems"]>(
		() =>
			routes === null
				? []
				: [
						...(routes.inventoryList && routes.offbrandInventoryList
							? [
									{
										key: "inventory",
										label: formatMessage(navigationMessages.inventoryList),
										url: routes?.inventoryList?.url(),

										childNav: [
											...toArray(routes.inventoryList.url(), "Porsche Vehicles", "inventoryList"),
											...toArray(
												routes.offbrandInventoryList.url(),
												"Non-Porsche Vehicles",
												"offbrandInventoryList",
											),
										],
									},
								]
							: [
									...toArray(
										routes?.inventoryList?.url(),
										navigationMessages.inventoryList,
										"inventoryList",
									),
								]),

						{
							key: "vehicleManagement",
							label: formatMessage(navigationMessages.vehicleManagement),
							url: routes.mediaList.url(),

							childNav: [
								{
									key: "vehicleMediaList",
									label: formatMessage(navigationMessages.vehicleMediaList),
									url: routes.mediaList.url(),
								},
								...(config.fulfillmentSalesEnabled !== "disabled"
									? toArray(
											routes.salesList?.url(),
											navigationMessages.salesList,
											"salesList",
											false,
											true,
										)
									: []),
								...(config.fulfillmentSalesEnabled !== "disabled"
									? toArray(
											routes.fulfillmentDeliveredVehiclesList?.url?.(),
											navigationMessages.fulfillmentDeliveredVehiclesList,
											"fulfillmentDeliveredVehiclesList",
										)
									: toArray(
											routes.deliveredVehiclesList?.url?.(),
											navigationMessages.deliveredVehiclesList,
											"deliveredVehiclesList",
										)),
								...toArray(
									routes.onlineReservations?.url(),
									navigationMessages.onlineReservations,
									"onlineReservations",
								),
								...toArray(
									routes.vehicleHistory?.url?.(),
									navigationMessages.vehicleHistory,
									"vehicleHistory",
									false,
								),
								...toArray(
									routes.inventoryIntegrationMonitor?.url?.(),
									navigationMessages.inventoryIntegrationMonitor,
									"inventoryIntegrationMonitor",
									false,
								),
								...(!routes.vehicleInformation
									? toArray(routes.external?.pcss.url(), navigationMessages.pcss, "pcss", true)
									: []),
							],
						},

						...toArray(
							routes.vehicleInformation?.url(),
							navigationMessages.vehicleInformation,
							"vehicleInformation",
							false,
						),

						...(routes.tradeInRequests || routes.purchaseRequests
							? [
									{
										key: "requests",
										label: formatMessage(navigationMessages.requests),
										url: routes.tradeInRequests?.url() ?? routes.purchaseRequests?.url() ?? "#",

										childNav: [
											...toArray(
												routes.tradeInRequests?.url(),
												navigationMessages.tradeIn,
												"tradeIn",
											),
											...toArray(
												routes.purchaseRequests?.url(),
												navigationMessages.purchaseRequests,
												"purchaseRequests",
											),
										],
									},
								]
							: []),

						...toArray(routes.buildsheets?.url(), navigationMessages.buildsheets, "buildsheets"),
						...(routes.external?.configurator
							? [
									{
										key: "configurator",
										label: formatMessage(navigationMessages.configuratorDealerMode),
										url: routes.external.configurator.url({
											backActionUrl: window.location.href,
											canReportSales: permissions.canReportSales === true,
											dealership: dealershipId,
											environment,
											locale,
										}),

										childNav: [
											...toArray(
												routes.external.configurator.url({
													backActionUrl: window.location.href,
													canReportSales: permissions.canReportSales === true,
													dealership: dealershipId,
													environment,
													locale,
												}),
												navigationMessages.configuratorDealerMode,
												"icc",
											),
											...(isFullOfferMode
												? []
												: toArray(
														routes.offers?.offerOverview?.url?.(),
														navigationMessages.savedConfigurationsOverview,
														"offerOverview",
														false,
													)),
										],
									},
								]
							: []),

						...(isFullOfferMode
							? toArray(
									routes.offers?.offerOverview?.url?.(),
									navigationMessages.offers,
									"offerOverview",
									false,
									true,
								)
							: []),

						...(routes.settings && Object.values(routes.settings).some((it) => it !== undefined)
							? [
									{
										key: "settings",
										label: formatMessage(navigationMessages.settings),
										url: routes.settings.inventoryLinks
											? routes.settings.inventoryLinks.url()
											: "#",

										childNav: [
											...toArray(
												routes.settings.account?.url?.() ??
													(routes.settings.account?.disabledHint
														? {
																disabledHint: routes.settings.account?.disabledHint,
															}
														: undefined),
												navigationMessages.accountSettings,
												"accountSettings",
												false,
											),
											...toArray(
												routes.settings.dealerContactDetails?.url(),
												navigationMessages.dealerContactDetailsSettings,
												"dealerContactDetails",
											),
											...toArray(
												routes.settings.dealerFeesAndAccessories?.url(),
												navigationMessages.dealerFeesAndAccessories,
												"dealerFeesAndAccessories",
											),
											...toArray(
												routes.settings.extraCosts?.url?.() ??
													(routes.settings.extraCosts?.disabledHint
														? {
																disabledHint: routes.settings.extraCosts?.disabledHint,
															}
														: undefined),
												navigationMessages.extraCosts,
												"extraCosts",
												false,
												config.extraCostsNewTag,
											),
											...toArray(
												routes.settings.contractualAgreements?.url?.() ??
													(routes.settings.contractualAgreements?.disabledHint
														? {
																disabledHint:
																	routes.settings.contractualAgreements?.disabledHint,
															}
														: undefined),
												navigationMessages.contractualAgreements,
												"contractualAgreements",
												false,
											),
											...toArray(
												routes.settings.inventoryLinks?.url(),
												navigationMessages.inventoryLinks,
												"inventoryLinks",
											),
											...toArray(
												routes.settings.notificationSettings?.url(),
												navigationMessages.notificationSettings,
												"notificationSettings",
											),
										],
									},
								]
							: []),

						...toArray(
							routes.analytics
								? `${window.location.origin}${prefixRoute(routes.analytics?.url())}`
								: undefined,
							navigationMessages.analytics,
							"analytics",
							true,
						),
					],
		[
			environment,
			config.extraCostsNewTag,
			config.fulfillmentSalesEnabled,
			dealershipId,
			formatMessage,
			isFullOfferMode,
			locale,
			prefixRoute,
			routes,
			permissions.canReportSales,
			toArray,
		],
	)

	const trackPcssLinkClick = useCallback(() => trackEvent({ action: "click_on_pcss", category: "outlink" }), [])
	const trackAnalyticsLinkClick = useCallback(
		() => trackEvent({ action: "click_on_analytics", category: "outlink" }),
		[],
	)

	return routes ? (
		<MainNavigation
			dealershipInfo={dealershipInfo}
			isCarSalesLite={config.carSalesLiteEnabled}
			isRTL={isRTL}
			logoLabel="Car Sales"
			logoLinkUrl={routes?.home?.url() ?? routes?.inventoryList?.url() ?? routes?.mediaList.url()}
			navItems={navItems}
			onAnalyticsLinkClick={trackAnalyticsLinkClick}
			onPcssLinkClick={trackPcssLinkClick}
			displayName={displayName}
			username={username}
			metaChildren={
				<>
					{config.notificationCenterEnabled && <NotificationCenterPopoverContainer />}
					{config.whatsNewEnabled ? <WhatsNewContainer /> : undefined}
				</>
			}
		/>
	) : null
}
